<template>
  <v-dialog
    max-width="800"
    :value="value"
    persistent
    :fullscreen="$vuetify.breakpoint.smAndDown"
    @keydown.esc="close(selectedItem)"
  >
    <v-card tile v-if="value">
      <v-app-bar dark flat color="primary">
        <v-btn icon dark @click="close(selectedItem)">
          <v-icon>close</v-icon>
        </v-btn>
        <span class="title">{{
          value.selectedItem
            ? resx.labels.updateContractor
            : resx.labels.newContractor
        }}</span>
      </v-app-bar>

      <form @submit.prevent="save()" novalidate>
        <v-card-text>
          <v-container grid-list-xl>
            <v-row wrap>
              <v-col cols="12" md="6">
                <v-row>
                  <v-col>
                    <v-text-field
                      :label="resx.labels.nameOrBusinessName"
                      v-model="selectedItem.name"
                      name="nameOrBusinessName"
                      :data-vv-as="resx.labels.nameOrBusinessName"
                      v-validate.initial="`required`"
                      :error-messages="errors.collect('nameOrBusinessName')"
                      required
                      autocomplete="off"
                    />
                  </v-col>
                </v-row>
              </v-col>

              <v-col cols="12" md="6">
                <v-row row wrap>
                  <v-col d-flex xs12 sm12 md4>
                    <v-autocomplete
                      data-vv-name="identifierType"
                      :data-vv-as="resx.labels.identifierType"
                      :error-messages="errors.collect('identifierType')"
                      v-validate.initial="'required'"
                      :items="identifierTypes"
                      v-model="selectedItem.identifier.type"
                      :label="resx.labels.identifierType"
                      item-text="text"
                      item-value="id"
                      cache-items
                      required
                      autocomplete="off"
                    ></v-autocomplete>
                  </v-col>
                  <v-col d-flex xs12 sm12 md8>
                    <v-text-field
                      :label="resx.labels.identifier"
                      v-model="selectedItem.identifier.number"
                      name="identifierNumber"
                      :data-vv-as="resx.labels.identifier"
                      :disabled="selectedItem.identifier.type == undefined"
                      type="text"
                      return-masked-value
                      :mask="identifierMask"
                      v-validate.initial="{
                        required: true,
                        uniqueid: [selectedItem._id],
                        identifierValid: [selectedItem.identifier.type]
                      }"
                      :error-messages="errors.collect('identifierNumber')"
                      required
                      autocomplete="off"
                    />
                  </v-col>
                </v-row>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="selectedItem.address"
                  :label="resx.labels.addressStreet"
                  name="address"
                  :data-vv-as="resx.labels.addressStreet"
                  v-validate.initial="'required'"
                  :error-messages="errors.collect('address')"
                  required
                  autocomplete="disabled"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="selectedItem.addressNumber"
                  :label="resx.labels.addressNumber"
                  name="addressNumber"
                  :data-vv-as="resx.labels.addressNumber"
                  v-validate.initial="'required|numeric|max_value:99999'"
                  :error-messages="errors.collect('addressNumber')"
                  required
                  autocomplete="disabled"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="selectedItem.floor"
                  :label="resx.labels.floor"
                  name="floor"
                  :data-vv-as="resx.labels.floor"
                  :error-messages="errors.collect('floor')"
                  required
                  autocomplete="disabled"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="selectedItem.office"
                  :label="resx.labels.office"
                  name="office"
                  :data-vv-as="resx.labels.office"
                  :error-messages="errors.collect('office')"
                  required
                  autocomplete="disabled"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="selectedItem.zipcode"
                  :label="resx.labels.zipcode"
                  name="zipcode"
                  :data-vv-as="resx.labels.zipcode"
                  v-validate.initial="'required|numeric|max_value:9999'"
                  :error-messages="errors.collect('zipcode')"
                  required
                  autocomplete="disabled"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="selectedItem.locality"
                  :label="resx.labels.locality"
                  name="locality"
                  :data-vv-as="resx.labels.locality"
                  v-validate.initial="'required'"
                  :error-messages="errors.collect('locality')"
                  required
                  autocomplete="disabled"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  data-vv-name="province"
                  :data-vv-as="resx.labels.province"
                  :error-messages="errors.collect('province')"
                  v-validate.initial="'required'"
                  :items="provinces"
                  v-model="selectedItem.province"
                  :label="resx.labels.province"
                  item-text="name"
                  item-value="name"
                  cache-items
                  required
                  autocomplete="disabled"
                />
              </v-col>
              <v-col cols="12" md="6">
                <datepicker-component
                  v-model="selectedItem.birthDate"
                  data-vv-as="birthDate"
                  data-vv-name="birthDate"
                  :label="resx.labels.birthDate"
                  :max="new Date().toISOString().substr(0, 10)"
                  min="1950-01-01"
                  noClear="true"
                  name="'birthDate'"
                  :required="true"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="selectedItem.contactName"
                  :label="resx.labels.contactName1"
                  name="contact"
                  :data-vv-as="resx.labels.contactName"
                  :error-messages="errors.collect('contact')"
                  autocomplete="disabled"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="selectedItem.contactName1"
                  :label="resx.labels.contactName2"
                  name="contact1"
                  :data-vv-as="resx.labels.contactName1"
                  :error-messages="errors.collect('contact1')"
                  autocomplete="disabled"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="selectedItem.contactPhone"
                  :label="resx.labels.contactPhone1"
                  name="contactPhone"
                  :data-vv-as="resx.labels.contactPhone"
                  :error-messages="errors.collect('contactPhone')"
                  autocomplete="disabled"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="selectedItem.contactPhone1"
                  :label="resx.labels.contactPhone2"
                  name="contactPhone1"
                  :data-vv-as="resx.labels.contactPhone1"
                  :error-messages="errors.collect('contactPhone1')"
                  autocomplete="disabled"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="selectedItem.contactEmail"
                  :label="resx.labels.contactEmail1"
                  name="contactEmail"
                  v-validate="'email'"
                  :data-vv-as="resx.labels.contactEmail"
                  :error-messages="errors.collect('contactEmail')"
                  autocomplete="disabled"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="selectedItem.contactEmail1"
                  :label="resx.labels.contactEmail2"
                  name="contactEmail1"
                  v-validate="'email'"
                  :data-vv-as="resx.labels.contactEmail1"
                  :error-messages="errors.collect('contactEmail1')"
                  autocomplete="disabled"
                />
              </v-col>
              <v-col cols="12">
                <comments-component
                  v-model="selectedItem.comments"
                  mode="edit"
                  :label="resx.labels.comments"
                  @input="selectedItem.comments = $event"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="close(selectedItem)">{{ resx.labels.cancel }}</v-btn>
          <v-btn class="primary" type="submit" :disabled="!isValidForm">{{
            resx.labels.ok
          }}</v-btn>
        </v-card-actions>
      </form>
    </v-card>
  </v-dialog>
</template>

<script>
"use strict";

import store from "@/store/store";
import { sprintf } from "sprintf-js";
import commentsComponent from "@/components/common/comments.vue";
import datepickerComponent from "@/components/common/datepicker.vue";
import contractorService from "@/services/ContractorService";
import validations from "@/utils/validations";
import moment from "moment";

const isValidEmail = {
  getMessage() {
    return store.state.locale.resx.messages.uniqueEmail;
  },
  async validate(value, args) {
    const isValid = await contractorService.isValidEmail(args[0], value);
    return {
      valid: isValid
    };
  }
};

const uniqueid = {
  getMessage() {
    return store.state.locale.resx.messages.uniqueIdentifier;
  },
  async validate(value, args) {
    const isValid = await contractorService.isValidId(args[0], value);
    return {
      valid: isValid ? isValid.result : false
    };
  }
};

const identifierValid = {
  getMessage() {
    return store.state.locale.resx.messages.identifierInvalid;
  },
  validate(value, args) {
    return validations.isValidIdentifer(value, args);
  }
};

export default {
  name: "edit-contractor",
  async mounted() {
    this.$validator.extend("isValidEmail", isValidEmail);
    this.$validator.extend("uniqueid", uniqueid);
    this.$validator.extend("identifierValid", identifierValid);
    this.identifierTypes = [
      {
        id: store.state.constants.IDENTIFIERS.CUIT,
        text: store.state.constants.IDENTIFIERS.CUIT.toUpperCase()
      },
      {
        id: store.state.constants.IDENTIFIERS.CUIL,
        text: store.state.constants.IDENTIFIERS.CUIL.toUpperCase()
      },
      {
        id: store.state.constants.IDENTIFIERS.DNI,
        text: store.state.constants.IDENTIFIERS.DNI.toUpperCase()
      }
    ];
    this.provinces = store.state.constants.PROVINCES;
  },
  components: {
    datepickerComponent,
    commentsComponent
  },
  data() {
    return {
      contractorTypes: [],
      identifierTypes: [],
      selectedItem: null,
      originalSelectedItem: null,
      identifierMask: "",
      provinces: [],
      currentDate:
        moment()
          .format("YYYY-MM-DDThh:mm:ss.SSS")
          .toString() + "Z"
    };
  },
  methods: {
    async save() {
      let result = null;
      let payload = Object.assign({}, this.selectedItem);
      if (!this.selectedItem._id) {
        result = await contractorService.create(payload);
        this.$emit("save", { isNew: true, item: result });
      } else {
        result = await contractorService.edit(payload);
        this.$emit("save", { isNew: false, item: result });
      }
    },
    async close(contractor) {
      if (JSON.stringify(this.originalSelectedItem) != JSON.stringify(this.selectedItem)) {
        await this.botConfirmDialog({
          title: `${
          this.resx.labels.edit
          } ${this.resx.labels.contractor.toLowerCase()}`,
          content: sprintf(
          this.resx.messages.noSavedChanges,
          contractor.name
          )
        })
        this.$emit("close");
      } else {
        this.$emit("close");
      }
    },
    assignMask(value) {
      switch (value) {
        case store.state.constants.IDENTIFIERS.CUIT:
          this.identifierMask = "##-########-#";
          break;
        case store.state.constants.IDENTIFIERS.CUIL:
          this.identifierMask = "##-########-#";
          break;
        case store.state.constants.IDENTIFIERS.DNI:
          this.identifierMask = "########";
          break;
        default:
          this.identifierMask = "";
          break;
      }
    }
  },
  props: ["value"],
  watch: {
    value(val) {
      this.selectedItem =
        val && val.selectedItem
          ? JSON.parse(JSON.stringify(val.selectedItem))
          : {
              identifier: { type: this.identifierTypes[0].id },
              birthDate: null
            };
      this.originalSelectedItem = Object.assign({}, this.selectedItem);
    },
    menu(val) {
      val && this.$nextTick(() => (this.$refs.picker.activePicker = "Año"));
    },
    "selectedItem.identifier.type"(val) {
      if (val) {
        this.assignMask(val);
      }
    }
  }
};
</script>
